import { Modal } from "@mui/material";
import { Close } from "@mui/icons-material";
import React, { useEffect, useState } from "react";

import { formatSlotTime, formatToDecimals } from "../../../helpers/dateFormat";
import { ErrorMessage } from "../../../commons/Toast/ErrorMessage";
import Styles from "./ScheduleModal.module.css";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const COL_WIDTHS = {
  time: "10%",
  volMw: "20%",
  volMwh: "15%",
};

const TABLE_HEADERS = [
  { label: "Start Time", width: COL_WIDTHS.time },
  { label: "End Time", width: COL_WIDTHS.time },
  { label: "Sch. Volume (MW)", width: COL_WIDTHS.volMw },
  { label: "Sch Volume (MWh)", width: COL_WIDTHS.volMwh },
  { label: "Del. Volume (MW)", width: COL_WIDTHS.volMw },
  { label: "Del. Volume (MWh)", width: COL_WIDTHS.volMwh },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const parseEndTime = (rawEnd) =>
  rawEnd?.trim() === "23:59:59" ? "24:00" : formatSlotTime(rawEnd?.trim());

const buildInitialBlocks = (blocks, type) =>
  blocks?.map((block) => {
    if (type === "deliver") {
      return {
        ...block,
        deliveredVolume: block?.scheduleVolume?.toFixed(2),
        deliveredEditVolume: block?.scheduleVolume?.toFixed(2),
      };
    }
    if (type === "edit") {
      return {
        ...block,
        scheduleEditVolume: block?.scheduleVolume?.toFixed(2),
      };
    }
    return block;
  });

const buildUpdatedBlocks = (viewUpdate, type) =>
  viewUpdate?.map(({ scheduleEditVolume, deliveredEditVolume, ...rest }) => ({
    ...rest,
    scheduleVolume: Number(
      type === "edit" ? scheduleEditVolume : rest.scheduleVolume,
    ),
    deliveredVolume: Number(
      type === "deliver" ? deliveredEditVolume : rest.deliveredVolume,
    ),
  }));

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
const TableHeader = () => (
  <div className="display_flex_row tableHeading_type2">
    {TABLE_HEADERS.map(({ label, width }) => (
      <div key={label} style={{ width }}>
        {label}
      </div>
    ))}
  </div>
);

const EditableInput = ({ value, onChange, styles }) => (
  <div className={styles.edit_input_schedule}>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={styles.editInput}
    />
  </div>
);

const ViewDetails = ({ ele, index, type, onVolumeChange }) => {
  const [scheduleMw, setScheduleMw] = useState((ele?.scheduleVolume || 0) * 4);
  const [deliveredMw, setDeliveredMw] = useState(
    type === "deliver" ? (ele?.scheduleVolume || 0) * 4 : 0,
  );

  const [startRaw, endRaw] = ele?.timeBlock?.split("-") ?? [];

  const sanitizeValue = (val) => {
    const num = Math.max(0, Number(val) || 0);
    return Math.round(num * 1000) / 1000;
  };

  const handleScheduleChange = (val) => {
    // Allow intermediate decimal input (e.g. "1.", "1.0", "1.23")
    if (/^\d*\.?\d{0,3}$/.test(val) && Number(val) >= 0) {
      const max = ele?.scheduleVolume * 4;
      if (Number(val) > max) {
        ErrorMessage(
          `Scheduled MW cannot exceed original schedule volume (${max} MW)`,
        );
        setScheduleMw(max);
        onVolumeChange(max / 4, index);
      } else {
        setScheduleMw(val); // keep as string while typing
        onVolumeChange(Number(val) / 4, index);
      }
    }
  };

  const handleDeliveredChange = (val) => {
    if (/^\d*\.?\d{0,3}$/.test(val) && Number(val) >= 0) {
      const max = ele?.scheduleVolume * 4;
      if (Number(val) > max) {
        ErrorMessage(
          `Delivered MW cannot exceed original schedule volume (${max} MW)`,
        );
        setDeliveredMw(max);
        onVolumeChange(max / 4, index);
      } else {
        setDeliveredMw(val); // keep as string while typing
        onVolumeChange(Number(val) / 4, index);
      }
    }
  };

  // Sanitize on blur to clean up trailing decimals like "1."
  const handleScheduleBlur = () => setScheduleMw(sanitizeValue(scheduleMw));
  const handleDeliveredBlur = () => setDeliveredMw(sanitizeValue(deliveredMw));

  return (
    <div className={`display_flex_row tableDetails ${Styles.tab_det_cont}`}>
      {/* Start Time */}
      <div style={{ width: COL_WIDTHS.time }}>{formatSlotTime(startRaw)}</div>

      {/* End Time */}
      <div style={{ width: COL_WIDTHS.time }}>{parseEndTime(endRaw)}</div>

      {/* Sch. Volume (MW) */}
      <div
        style={{ width: COL_WIDTHS.volMw }}
        className="display_flex_row_gap_8"
      >
        {type === "edit" ? (
          <EditableInput
            value={scheduleMw}
            onChange={handleScheduleChange}
            styles={Styles}
          />
        ) : (
          <div className={Styles.text_input_head}>
            {Number(scheduleMw).toFixed(2)}
          </div>
        )}
      </div>

      {/* Sch. Volume (MWh) */}
      <div
        style={{ width: COL_WIDTHS.volMwh }}
        className="display_flex_row_gap_8"
      >
        {formatToDecimals(ele?.scheduleEditVolume ?? ele?.scheduleVolume)}
      </div>

      {/* Del. Volume (MW) */}
      <div
        style={{ width: COL_WIDTHS.volMw }}
        className="display_flex_row_gap_8"
      >
        {type === "deliver" ? (
          <EditableInput
            value={deliveredMw}
            onChange={handleDeliveredChange}
            styles={Styles}
          />
        ) : (
          <div className={Styles.text_input_head}>
            {Number(deliveredMw).toFixed(2)}
          </div>
        )}
      </div>

      {/* Del. Volume (MWh) */}
      <div style={{ width: COL_WIDTHS.volMwh }}>
        {formatToDecimals(ele?.deliveredEditVolume ?? ele?.deliveredVolume)}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
const ScheduleUpdateModal = ({
  type,
  open,
  handleClose,
  blocks,
  handleUpdateSubmit,
}) => {
  const [viewUpdate, setViewUpdate] = useState(blocks);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setViewUpdate(buildInitialBlocks(blocks, type));
  }, [blocks, type]);

  const handleVolumeChange = (val, index) => {
    const field =
      type === "edit" ? "scheduleEditVolume" : "deliveredEditVolume";
    setViewUpdate((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: val };
      return updated;
    });
  };

  const handleUpdate = () => {
    setLoading(true);
    Promise.resolve(
      handleUpdateSubmit(buildUpdatedBlocks(viewUpdate, type)),
    ).finally(() => setLoading(false));
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="child-modal-title"
      aria-describedby="child-modal-description"
    >
      <div className={`popupModal ${Styles.scheduleEditModal}`}>
        {/* Header */}
        <div className={`display_flex_row modalHeading ${Styles.modalHeading}`}>
          <div className="headingText">
            {type === "edit" ? "Edit Schedule" : "Confirm Schedule"}
          </div>
          <Close
            style={{ fontSize: 24, color: "#777777", cursor: "pointer" }}
            onClick={handleClose}
          />
        </div>

        {/* Table */}
        <div className={Styles.scheduleEditMainCont}>
          <TableHeader />
          <div className={`scroll_bar_y ${Styles.scrollMainSection}`}>
            {viewUpdate?.map((ele, index) => (
              <ViewDetails
                key={index}
                ele={ele}
                index={index}
                type={type}
                onVolumeChange={handleVolumeChange}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className={`display_flex_row_center ${Styles.buttonMainCont}`}>
          <button
            style={{ width: "200px", height: "40px", border: "0px" }}
            onClick={handleUpdate}
            className={`primary-button ${Styles.buttonList}`}
            disabled={loading}
          >
            {loading ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ScheduleUpdateModal;
